// src/components/mpi/CentralGauge.js
// Extracted from MPIDashboardPage.js (lines 16-113) for reuse across dashboards.

import { useState, useEffect } from 'react';

/* Score-abhängige Farbe für Donuts und Zahlenwerte */
export function dimColor(v) {
  if (v == null) return '#475569';
  if (v < 55) return '#EF4444';
  if (v < 70) return '#F59E0B';
  return '#10B981';
}

/* Animierter Hochzähler: 0 → target über 1400ms mit quartischem Ease-out */
export function useCountUp(target, duration = 1400) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!target) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 4);
      setVal(target * ease);
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration]);
  return val;
}

export default function CentralGauge({ score, prev, label, segmentColors }) {
  const animated = useCountUp(score);
  const scoreIsNull = score == null;
  const trend = (score != null && prev != null) ? score - prev : null;
  const r = 96;
  const circ = 2 * Math.PI * r;
  const dash = circ * (animated / 100);
  const gap = circ - dash;
  const col = dimColor(score);

  // Segments clockwise from 12 o'clock: top-right, bottom-right, bottom-left, top-left
  // segmentColors prop order: [D1, D2, D3, D4]
  // Mapping: top-right=D2(1), bottom-right=D4(3), bottom-left=D3(2), top-left=D1(0)
  const segColorIdx = [1, 3, 2, 0];
  const defaultSegs = [
    { color: '#F59E0B', start: 0,    end: 0.23 },
    { color: '#3B82F6', start: 0.25, end: 0.48 },
    { color: '#10B981', start: 0.50, end: 0.73 },
    { color: '#64748B', start: 0.75, end: 0.98 },
  ];

  const segs = segmentColors
    ? defaultSegs.map((s, i) => ({ ...s, color: segmentColors[segColorIdx[i]] || s.color }))
    : defaultSegs;

  return (
    <div style={{ position: 'relative', width: 240, height: 240, flexShrink: 0 }}>
      <svg width={240} height={240} style={{ position: 'absolute', inset: 0 }}>
        {segs.map((s, i) => {
          const segLen = circ * (s.end - s.start);
          const rotDeg = -90 + s.start * 360;
          return (
            <circle key={i} cx={120} cy={120} r={r + 18}
              fill="none" stroke={s.color} strokeWidth={3}
              strokeDasharray={`${segLen * 0.88} ${circ}`}
              strokeLinecap="round" opacity={0.35}
              style={{ transform: `rotate(${rotDeg}deg)`, transformOrigin: '120px 120px' }}
            />
          );
        })}
        <circle cx={120} cy={120} r={r} fill="none"
          stroke="rgba(255,255,255,0.06)" strokeWidth={14}
          style={{ transform: 'rotate(-90deg)', transformOrigin: '120px 120px' }}
        />
        <circle cx={120} cy={120} r={r} fill="none"
          stroke={col} strokeWidth={14}
          strokeDasharray={`${dash} ${gap}`} strokeLinecap="round"
          style={{
            transform: 'rotate(-90deg)', transformOrigin: '120px 120px',
            filter: `drop-shadow(0 0 14px ${col}99)`,
            transition: 'stroke-dasharray 0.05s linear',
          }}
        />
        <circle cx={120} cy={120} r={r - 22} fill="none"
          stroke="rgba(255,255,255,0.04)" strokeWidth={1}
        />
      </svg>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ fontSize: 9, letterSpacing: '0.28em', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', marginBottom: 2 }}>
          {label || 'MPI Score'}
        </div>
        {scoreIsNull && (
          <div style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.2, color: '#64748B', marginTop: 6, textAlign: 'center' }}>
            keine Daten
          </div>
        )}
        {!scoreIsNull && (
        <div style={{
          fontSize: 62, fontWeight: 800, lineHeight: 1, letterSpacing: '-3px',
          color: col,
          fontFamily: "'SF Pro Display', -apple-system, sans-serif",
          textShadow: `0 0 32px ${col}55`,
        }}>
          {animated.toFixed(0)}
        </div>
        )}
        {trend != null && (
          <div style={{ fontSize: 11, color: trend < 0 ? '#EF4444' : '#10B981', marginTop: 5, display: 'flex', alignItems: 'center', gap: 3 }}>
            <span style={{ fontSize: 9 }}>{trend < 0 ? '▼' : '▲'}</span>
            {Math.abs(trend).toFixed(1)} ggü. Vorquartal
          </div>
        )}
      </div>
    </div>
  );
}
